import React, { useEffect, useState } from 'react';
import { useJsApiLoader } from '@react-google-maps/api';
import { googleMapsConfig } from 'config/constants';

const GOOGLE_MAPS_SCRIPT_ID = 'fitgroup-google-maps-script';
const GOOGLE_MAPS_LIBRARIES = ['places'];

export const isGoogleMapsMapReady = (targetWindow = window) => (
  !!targetWindow?.google?.maps
  && typeof targetWindow.google.maps.Map === 'function'
);

export const isGoogleMapsApiReady = (targetWindow = window) => (
  isGoogleMapsMapReady(targetWindow)
  && typeof targetWindow.google.maps.places?.Autocomplete === 'function'
);

export const ensureGoogleMapsPlacesLoaded = async (targetWindow = window) => {
  if (isGoogleMapsApiReady(targetWindow)) {
    return true;
  }

  const importLibrary = targetWindow?.google?.maps?.importLibrary;
  if (typeof importLibrary !== 'function') {
    return false;
  }

  await importLibrary('places');
  return isGoogleMapsApiReady(targetWindow);
};

function GoogleMapsAlreadyLoaded({ children, loadingElement, onLoad }) {
  const [isApiReady, setIsApiReady] = useState(() => (
    typeof window !== 'undefined' && isGoogleMapsApiReady(window)
  ));

  useEffect(() => {
    let isMounted = true;

    ensureGoogleMapsPlacesLoaded(window).then((ready) => {
      if (!isMounted) return;
      setIsApiReady(ready);
      if (ready && onLoad) {
        onLoad();
      }
    });

    return () => {
      isMounted = false;
    };
  }, [onLoad]);

  if (!isApiReady) {
    return loadingElement || null;
  }

  return children;
}

function useGoogleMapsPlacesReady(isLoaded, onLoad) {
  const [isApiReady, setIsApiReady] = useState(() => (
    typeof window !== 'undefined' && isGoogleMapsApiReady(window)
  ));

  useEffect(() => {
    if (!isLoaded) return undefined;

    let isMounted = true;
    let intervalId;

    const checkReadiness = async () => {
      if (typeof window === 'undefined') return;

      const ready = await ensureGoogleMapsPlacesLoaded(window);
      if (!isMounted) return;

      if (ready) {
        setIsApiReady(true);
        if (onLoad) {
          onLoad();
        }
        if (intervalId) {
          clearInterval(intervalId);
        }
      }
    };

    checkReadiness();

    if (typeof window !== 'undefined' && !isGoogleMapsApiReady(window)) {
      intervalId = setInterval(checkReadiness, 100);
    }

    return () => {
      isMounted = false;
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isLoaded, onLoad]);

  return isApiReady;
}

function GoogleMapsHookLoader({ children, loadingElement, onLoad, onError }) {
  const { isLoaded, loadError } = useJsApiLoader({
    id: GOOGLE_MAPS_SCRIPT_ID,
    googleMapsApiKey: googleMapsConfig.apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });
  const isApiReady = useGoogleMapsPlacesReady(isLoaded, onLoad);

  useEffect(() => {
    if (loadError && onError) {
      onError(loadError);
    }
  }, [loadError, onError]);

  if (loadError) {
    return loadingElement || null;
  }

  if (!isLoaded || !isApiReady) {
    return loadingElement || null;
  }

  return children;
}

export default function GoogleMapsLoader(props) {
  if (typeof window !== 'undefined' && isGoogleMapsMapReady(window)) {
    return <GoogleMapsAlreadyLoaded {...props} />;
  }

  return <GoogleMapsHookLoader {...props} />;
}
